import { getFontEmbedCSS } from 'html-to-image';
import { embedResources, parseURLs } from 'html-to-image/lib/embed-resources';

const embeddedFaces = new Map<string, Promise<string>>();
const MAX_CACHED_FACES = 64;

function families(value: string) {
  const names: string[] = [];
  const pattern =
    /\s*(?:"((?:\\.|[^"])*)"|'((?:\\.|[^'])*)'|((?:\\.|[^,])+))\s*(?:,|$)/g;
  for (const match of Array.from(value.matchAll(pattern))) {
    names.push(
      (match[1] ?? match[2] ?? match[3])
        .trim()
        .replace(
          /\\([\da-f]{1,6})\s?|\\([\s\S])/gi,
          (_escape, hex, literal) => {
            const code = parseInt(hex, 16);
            return hex
              ? String.fromCodePoint(
                  code > 0 && code <= 0x10ffff ? code : 0xfffd
                )
              : literal;
          }
        )
        .toLowerCase()
    );
  }
  return names;
}

function includesCodePoint(range: string, characters: Set<number>) {
  if (!range) return true;
  return range.split(',').some((part) => {
    const match = part.trim().match(/^U\+([\dA-F?]+)(?:-([\dA-F]+))?$/i);
    // Unknown descriptors must keep the face, never silently omit glyphs.
    if (!match) return true;
    const first = parseInt(match[1].replace(/\?/g, '0'), 16);
    const last = parseInt(match[2] || match[1].replace(/\?/g, 'F'), 16);
    return Array.from(characters).some((code) => code >= first && code <= last);
  });
}

// Capture libraries otherwise embed unrelated fonts, every Unicode subset and
// both WOFF/WOFF2, expanding document imports again on every source/backdrop.
export async function getLinearLightFontEmbedCSS(node: HTMLElement) {
  const usage = new Map<string, Set<number> | null>();
  const record = (style: CSSStyleDeclaration, text: string) => {
    for (const family of families(style.getPropertyValue('font-family'))) {
      // CSS transforms can select a different Unicode face than the source text.
      // Keep all subsets for this family when presentation changes its glyphs.
      const transform = style.getPropertyValue('text-transform');
      if (usage.get(family) === null || (transform && transform !== 'none')) {
        usage.set(family, null);
        continue;
      }
      const characters = usage.get(family) || new Set<number>();
      for (const character of Array.from(text))
        characters.add(character.codePointAt(0)!);
      usage.set(family, characters);
    }
  };
  const add = (element: Element, text: string, pseudo?: string) => {
    if (!text) return;
    record(getComputedStyle(element, pseudo), text);
    // Ancestor fonts can determine normal line-height and inline/SVG baselines.
    // Preserve those metrics while pruning unrelated faces and Unicode subsets.
    if (pseudo) record(getComputedStyle(element), text);
    for (
      let parent = element.parentElement;
      parent && node.contains(parent);
      parent = parent.parentElement
    )
      record(getComputedStyle(parent), text);
  };
  for (const element of [node, ...Array.from(node.querySelectorAll('*'))]) {
    if (element.closest('style, script')) continue;
    const source = element.closest<HTMLElement>('[data-linear-light-source]');
    // Ready layers already paint a PNG. Their concealed editable text is not
    // drawn by backdrop capture; a source snapshot restores opacity first.
    if (source && source.style.opacity === '0') continue;
    const text = Array.from(element.childNodes)
      .filter((child) => child.nodeType === Node.TEXT_NODE)
      .map((child) => child.textContent || '')
      .join('');
    add(element, text);
    if (!(element instanceof HTMLElement)) continue;
    for (const pseudo of ['::before', '::after']) {
      const content = getComputedStyle(element, pseudo).getPropertyValue(
        'content'
      );
      if (!content || content === 'none' || content === 'normal') continue;
      if (!/^(["']).*\1$/.test(content) || content.includes('\\'))
        return getFontEmbedCSS(node);
      add(element, content.slice(1, -1), pseudo);
    }
  }
  if (!usage.size) return '';

  const faces = new Map<string, { css: string; base: string | null }>();
  const visit = (rules: CSSRuleList, base: string | null) => {
    for (const rule of Array.from(rules)) {
      if (rule.type === CSSRule.FONT_FACE_RULE) {
        const face = rule as CSSFontFaceRule;
        const family = families(face.style.getPropertyValue('font-family'))[0];
        const characters = usage.get(family);
        if (
          !usage.has(family) ||
          (characters &&
            !includesCodePoint(
              face.style.getPropertyValue('unicode-range'),
              characters
            ))
        )
          continue;
        const src = face.style.getPropertyValue('src');
        const woff2 = src.match(
          /url\([^)]*\)\s*format\(\s*["']?woff2["']?\s*\)/i
        )?.[0];
        // Select per face without the library's stateful global format regex.
        // A face without WOFF2 retains its original sources (including TTF/OTF).
        const css = woff2
          ? face.cssText.replace(src, () => woff2)
          : face.cssText;
        // Imported rules may also have been inlined by previous captures.
        // Absolute resources resolve identically regardless of the parent sheet.
        const faceBase = parseURLs(css).every((url) =>
          /^[a-z][a-z\d+.-]*:/i.test(url)
        )
          ? null
          : base;
        faces.set(JSON.stringify([faceBase, css]), { css, base: faceBase });
      } else if (rule.type === CSSRule.IMPORT_RULE) {
        const sheet = (rule as CSSImportRule).styleSheet;
        visit(sheet.cssRules, sheet.href || base);
      } else if ('cssRules' in rule) {
        if (
          rule.type === CSSRule.MEDIA_RULE &&
          !window.matchMedia((rule as CSSMediaRule).conditionText).matches
        )
          continue;
        if (
          rule.type === CSSRule.SUPPORTS_RULE &&
          !CSS.supports((rule as CSSSupportsRule).conditionText)
        )
          continue;
        visit((rule as CSSGroupingRule).cssRules, base);
      }
    }
  };
  try {
    for (const sheet of Array.from(node.ownerDocument.styleSheets))
      visit(sheet.cssRules, sheet.href || null);
  } catch {
    // Cross-origin/unready CSS can contain required faces. Preserve the existing
    // fetch/embedding fallback instead of treating inaccessible rules as empty.
    return getFontEmbedCSS(node);
  }

  return (
    await Promise.all(
      Array.from(faces, ([key, { css, base }]) => {
        let pending = embeddedFaces.get(key);
        if (!pending) {
          // Keep query-based font revisions distinct in the library URL cache.
          pending = embedResources(css, base, { includeQueryParams: true });
          embeddedFaces.set(key, pending);
          if (embeddedFaces.size > MAX_CACHED_FACES)
            embeddedFaces.delete(embeddedFaces.keys().next().value!);
          const forget = () => {
            if (embeddedFaces.get(key) === pending) embeddedFaces.delete(key);
          };
          // The library may retain an external URL instead of rejecting a failed
          // download. Do not make that incomplete embedding a permanent cache hit.
          pending.then((embedded) => {
            if (
              parseURLs(embedded).length ||
              /url\(\s*["']?\s*(?:data:[^,)]*,\s*)?["']?\s*\)/.test(embedded)
            )
              forget();
          }, forget);
        }
        return pending;
      })
    )
  ).join('\n');
}
